import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading } from '../Loading';
import styles from './GameManagement.module.css';
import { Game, getAllGames } from '../../services/getAllGames.service';
import {
  getAllProviders,
  Provider,
} from '../../services/getAllProviders.service';
import {
  getAllEnvironments,
  Environment,
} from '../../services/getAllEnvironment.service';
import { addGameAssignment } from '../../services/addGameAssignment.service';
import { removeGameAssignment } from '../../services/removeGameAssignment.service';
import {
  areAllGamesEnabledForLabel,
  activateAllGamesForLabel,
  deactivateAllGamesForLabel,
  buildProviderLabelGamesInput,
} from '../../services/gameLabelBulk.service';
import { authService } from '../../services/auth.service';
import {
  getGameHistory,
  GameHistory,
} from '../../services/getGameHistory.service';
import {
  getAllLabelsByEnvironment,
  Label,
} from '../../services/getAllLabelsByEnvironment.service';
import { formatDate } from '../../utils/helpers/date.helpers';
import { calculateProfitLoss } from '../../utils/helpers/calculation.helpers';
import { updateGamePosition, updateGameComment } from '../../services/updateGamePosition.service';
import { updateGameOrientation, updateGameCategoryType } from '../../services/updateGameStatusById.service';
import { EnterGameModal } from '../EnterGame/EnterGame';
import { getAllBrands } from '../../services/getAllBrands.service';
import GameIconPreview from './GameIconPreview';
import { fileUploadService, uploadGameAsset } from '../../services/fileUpload.service';

const LOCAL_STORAGE_KEY = 'gameManagementSelections';

const GameManagement = () => {
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [brandname, setBrandname] = useState<string>('');
  const [selectedProviderId, setSelectedProviderId] = useState<number | 'ALL'>(
    'ALL',
  );
  const [selectedProviderName, setSelectedProviderName] = useState<string>('');
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<number>(2);
  const [selectedLabels, setSelectedLabels] = useState<string>('');
  const [labels, setLabels] = useState<Label[]>([]);
  const [selectedLabelId, setSelectedLabelId] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [updatingGameId, setUpdatingGameId] = useState<number | null>(null);
  const [allGamesEnabled, setAllGamesEnabled] = useState(false);
  const [updatingAllGames, setUpdatingAllGames] = useState(false);
  const [gameHistory, setGameHistory] = useState<GameHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editingPosition, setEditingPosition] = useState<number | null>(null);
  const [positionInput, setPositionInput] = useState<string>('');
  const [positionUpdating, setPositionUpdating] = useState(false);
  const [positionSuccess, setPositionSuccess] = useState(false);
  const [dateNow, setDateNow] = useState<any>(Date.now());
  const [gameIconUrl, setGameIconUrl] = useState<any>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const labelIconInputRef = useRef<HTMLInputElement>(null);
  const [labelIconUpdating, setLabelIconUpdating] = useState(false);

  // Infinite scrolling state
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);


  // Ref to hold restored selections
  const restoredSelections = useRef<any>(null);
  // Ref for game list container for auto-scroll
  const gameListRef = useRef<HTMLDivElement>(null);
  const [restorationComplete, setRestorationComplete] = useState(false);
  const user = authService.getStoredUser();

  // Enter Game Modal state
  const [isEnterGameOpen, setIsEnterGameOpen] = useState(false);
  const [enterGameInfo, setEnterGameInfo] = useState<{
    id: number;
    gameId: string;
    userId: number;
    providerId: number;
    gameName?: string;
    environmentId: number;
    labelId: number;
  } | null>(null);



  // Restore selections from localStorage on mount (before fetching data)
  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        restoredSelections.current = JSON.parse(saved);
      } catch (e) {
        restoredSelections.current = null;
      }
    }
    setRestorationComplete(true);
  }, []);

  // Fetch providers only once after restoration
  useEffect(() => {
    if (!restorationComplete) return;
    getAllProviders()
      .then((providers) => {
        setProviders(providers);
        // Use restored value if valid, else default
        if (
          restoredSelections.current &&
          restoredSelections.current.selectedProviderId !== undefined
        ) {
          setSelectedProviderId(restoredSelections.current.selectedProviderId);
          setSelectedProviderName(
            restoredSelections.current.selectedProviderName || 'ALL',
          );
        } else {
          setSelectedProviderId('ALL');
          setSelectedProviderName('ALL');
        }
      })
      .catch((error) => {
        console.error('Error fetching providers:', error);
      });
  }, [restorationComplete]);

  // Fetch brands only once after restoration
  useEffect(() => {
    if (!restorationComplete) return;
    const providerId = selectedProviderId === 'ALL' ? undefined : selectedProviderId;
    getAllBrands(providerId)
      .then((brands) => {
        setBrands(['ALL', ...brands]);
        // Use restored value if valid, else default
        if (
          restoredSelections.current &&
          restoredSelections.current.selectedBrand !== undefined
        ) {
          setBrandname(restoredSelections.current.selectedBrand);
        } else {
          setBrandname('ALL');
        }
      })
      .catch((error) => {
        console.error('Error fetching providers:', error);
      });
  }, [restorationComplete, selectedProviderId]);

  // Fetch environments and labels after restoration
  useEffect(() => {
    if (!restorationComplete) return;
    getAllEnvironments()
      .then((environments) => {
        setEnvironments(environments);
        let envId = environments.length > 0 ? environments[0].id : undefined;
        if (
          restoredSelections.current &&
          restoredSelections.current.selectedEnvironmentId !== undefined
        ) {
          const found = environments.find(
            (e) => e.id === restoredSelections.current.selectedEnvironmentId,
          );
          if (found) envId = found.id;
        }
        if (envId !== undefined) {
          setSelectedEnvironmentId(envId);
          fetchLabelsByEnvironment(envId).then((labelsData) => {
            setLabels(labelsData);
            // Use restored label if valid, else default
            let labelId = labelsData.length > 0 ? labelsData[0].id : undefined;
            if (
              restoredSelections.current &&
              restoredSelections.current.selectedLabelId !== undefined
            ) {
              const foundLabel = labelsData.find(
                (l) => l.id === restoredSelections.current.selectedLabelId,
              );
              if (foundLabel) labelId = foundLabel.id;
            }
            if (labelId !== undefined) {
              setSelectedLabelId(labelId);
            }
          });
        }
      })
      .catch((error) => {
        console.error('Error fetching environments:', error);
      });
  }, [restorationComplete]);

  // Restore category if present
  useEffect(() => {
    if (!restorationComplete) return;
    if (
      restoredSelections.current &&
      restoredSelections.current.selectedCategory !== undefined
    ) {
      setSelectedCategory(restoredSelections.current.selectedCategory);
    }
  }, [restorationComplete]);

  // Debounce search query with 500ms delay
  useEffect(() => {
    if (searchQuery !== debouncedSearchQuery) {
      setIsSearching(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setIsSearching(false);
    }, 700);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (selectedProviderId === 'ALL') {
      setAllGamesEnabled(false);
      return;
    }

    let cancelled = false;

    areAllGamesEnabledForLabel(
      buildProviderLabelGamesInput(
        selectedProviderId,
        selectedLabelId,
        brandname,
        selectedCategory,
      ),
    )
      .then((enabled) => {
        if (!cancelled) {
          setAllGamesEnabled(enabled);
        }
      })
      .catch((error) => {
        console.error('Error checking all games enabled status:', error);
        if (!cancelled) {
          setAllGamesEnabled(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProviderId, selectedLabelId, brandname, selectedCategory]);

  // Reset pagination when filters change
  useEffect(() => {
    // No need to check for selectedProviderId === undefined, allow 'ALL'
    setGames([]);
    setEndCursor(null);
    setHasNextPage(false);
    setInitialLoadComplete(false);
    setSelectedGame(null); // Clear selected game when filters change
    setGameIconUrl(null)

    // Load initial games
    loadGames();
  }, [
    selectedProviderId,
    selectedLabelId,
    selectedEnvironmentId,
    selectedCategory,
    brandname,
    debouncedSearchQuery,
  ]);

  // When selectedGame changes, reset position editing state
  useEffect(() => {
    setEditingPosition(null);
    setPositionInput(selectedGame ? String(selectedGame.position) : '');
    setPositionSuccess(false);
  }, [selectedGame]);


  // Save selections to localStorage whenever they change
  useEffect(() => {
    if (!restorationComplete) return;
    const toSave = {
      selectedProviderId,
      selectedProviderName,
      selectedLabelId,
      selectedEnvironmentId,
      selectedCategory,
      selectedGameId: selectedGame?.id || null,
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(toSave));
  }, [
    selectedProviderId,
    selectedProviderName,
    selectedLabelId,
    selectedEnvironmentId,
    selectedCategory,
    selectedGame,
    restorationComplete,
  ]);

  // After games are loaded, restore selectedGame if present in localStorage
  // Skip if we already have a selected game (e.g., when loading more)
  useEffect(() => {
    if (!restorationComplete) return;
    // Skip if we already have a selected game (loading more scenario)
    if (selectedGame && games.find((g) => g.id === selectedGame.id)) {
      return;
    }
    if (games.length > 0) {
      let selectedGameId: string | number | null = null;
      if (
        restoredSelections.current &&
        restoredSelections.current.selectedGameId
      ) {
        selectedGameId = restoredSelections.current.selectedGameId;
      }
      if (selectedGameId !== null && selectedGameId !== undefined) {
        const found = games.find((g) => g.id === selectedGameId);
        if (found) {
          setSelectedGame(found);
          setGameIconUrl(found?.iconUrl + '?t=' + Date.now())
          fetchGameHistory(found.gameId);
          return;
        }
      }
      // If no saved game or not found, select first game as before
      setSelectedGame(games[0]);
      setGameIconUrl(games[0]?.iconUrl + '?t=' + Date.now())

      fetchGameHistory(games[0].gameId);
    } else {
      setSelectedGame(null);
      setGameIconUrl(null)

    }
  }, [games, restorationComplete]);

  const loadGames = async (isLoadMore = false) => {
    // Don't send provider/category if 'ALL' is selected
    let providerIdToSend: number | undefined =
      selectedProviderId === 'ALL' ? undefined : selectedProviderId;
    let categoryToSend: string | undefined =
      selectedCategory === 'ALL' ? undefined : selectedCategory;
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else if (!debouncedSearchQuery) {
        // Only show full loader if not searching
        setLoading(true);
      }

      const result = await getAllGames(
        selectedLabelId,
        selectedEnvironmentId,
        providerIdToSend,
        100,
        isLoadMore && endCursor ? endCursor : undefined,
        categoryToSend,
        brandname,
        debouncedSearchQuery || undefined,
      );

      const gamesList = result.games || [];

      if (isLoadMore) {
        // Save scroll position before updating state
        const savedScrollTop = gameListRef.current?.scrollTop || 0;
        setGames((prevGames) => [...prevGames, ...gamesList]);
        // Restore scroll position after React updates DOM
        requestAnimationFrame(() => {
          if (gameListRef.current) {
            gameListRef.current.scrollTop = savedScrollTop;
          }
        });
      } else {
        setGames(gamesList);
        // Set the first game as selected by default when filters change (not just initial load)
        if (gamesList.length > 0) {
          const firstGame = gamesList[0];
          setSelectedGame(firstGame);
          setGameIconUrl(firstGame.iconUrl + '?t=' + Date.now())
          // Fetch history for the first game
          fetchGameHistory(firstGame.gameId);
          setInitialLoadComplete(true);
        } else {
          // No games found, clear selected game
          setSelectedGame(null);
          setGameIconUrl(null)
        }
      }

      // Update pagination state
      setHasNextPage(result.pageInfo.hasNextPage);
      setEndCursor(result.pageInfo.endCursor || null);
    } catch (error) {
      console.error('Error fetching games:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    // Load more when user scrolls to bottom (with 50px threshold)
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      if (hasNextPage && !loadingMore && !loading) {
        loadGames(true);
      }
    }
  };

  const handleGameSelect = (game: Game) => {
    setSelectedGame(game);
    setGameIconUrl(game.iconUrl + '?t=' + Date.now())
    // Fetch game history for the selected game
    fetchGameHistory(game.gameId);
  };

  const fetchGameHistory = async (gameId: string) => {
    try {
      setHistoryLoading(true);
      const history = await getGameHistory(gameId);
      setGameHistory(history);
    } catch (error) {
      console.error('Error fetching game history:', error);
      setGameHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchLabelsByEnvironment = async (environmentId: number) => {
    try {
      const labelsData = await getAllLabelsByEnvironment(environmentId);
      return labelsData; // Return the data for the next then
    } catch (error) {
      console.error('Error fetching labels by environment:', error);
      return [];
    }
  };

  const handleProviderSelect = (
    providerId: number | 'ALL',
    providerName: string,
  ) => {
    setSelectedProviderId(providerId);
    setSelectedProviderName(providerName);
    setSelectedGame(null); // Reset selected game when provider changes
    setGameIconUrl(null)
  };

  const handleToggleAllGamesStatus = async (event: React.MouseEvent) => {
    event.stopPropagation();

    if (
      selectedProviderId === 'ALL' ||
      updatingAllGames ||
      updatingGameId !== null
    ) {
      return;
    }

    const bulkInput = buildProviderLabelGamesInput(
      selectedProviderId,
      selectedLabelId,
      brandname,
      selectedCategory,
    );
    const newActiveStatus = !allGamesEnabled;

    try {
      setUpdatingAllGames(true);

      if (newActiveStatus) {
        await activateAllGamesForLabel(bulkInput);
      } else {
        await deactivateAllGamesForLabel(bulkInput);
      }

      setAllGamesEnabled(newActiveStatus);
      setGames((prevGames) =>
        prevGames.map((g) => ({ ...g, isActive: newActiveStatus })),
      );

      if (selectedGame) {
        setSelectedGame({ ...selectedGame, isActive: newActiveStatus });
      }
    } catch (error) {
      console.error('Error updating all games status:', error);
    } finally {
      setUpdatingAllGames(false);
    }
  };

  const handleToggleGameStatus = async (
    game: Game,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation(); // Prevent game selection when clicking toggle

    if (updatingGameId === game.id) {
      return; // Prevent multiple simultaneous updates
    }

    try {
      setUpdatingGameId(game.id);
      const newActiveStatus = !game.isActive;

      let updatedGame: Game;

      if (newActiveStatus) {
        // Activate game - add game assignment
        updatedGame = await addGameAssignment({
          gameId: game.id,
          environmentId: selectedEnvironmentId,
          labelId: selectedLabelId,
          position: game.position || 1, // Use game position or default to 1
        });
      } else {
        // Deactivate game - remove game assignment
        updatedGame = await removeGameAssignment({
          gameId: game.id,
          environmentId: selectedEnvironmentId,
          labelId: selectedLabelId,
        });
      }

      // Update the games list with the new data
      // (a new or removed assignment never carries a label icon)
      setGames((prevGames) =>
        prevGames.map((g) =>
          g.id === game.id
            ? {
                ...g,
                isActive: updatedGame.isActive,
                iconUrl: updatedGame.iconUrl,
                labelIconUrl: null,
              }
            : g,
        ),
      );

      // Update selected game if it's the one being toggled
      if (selectedGame?.id === game.id) {
        setSelectedGame(updatedGame);
        setGameIconUrl(updatedGame.iconUrl + '?t=' + Date.now())
      }

      if (selectedProviderId !== 'ALL') {
        const enabled = await areAllGamesEnabledForLabel(
          buildProviderLabelGamesInput(
            selectedProviderId,
            selectedLabelId,
            brandname,
            selectedCategory,
          ),
        );
        setAllGamesEnabled(enabled);
      }
    } catch (error) {
      console.error('Error updating game status:', error);
      // You could add a toast notification here to show the error to the user
    } finally {
      setUpdatingGameId(null);
    }
  };

  const handlePlayDemo = (game: Game) => {
    const currentUser = authService.getStoredUser();
    const userId = currentUser?.id || 1;
    setEnterGameInfo({
      id: game.id,
      gameId: game.gameId,
      userId: userId,
      providerId: game.providerId,
      gameName: game.name,
      environmentId: selectedEnvironmentId,
      labelId: selectedLabelId,
    });
    setIsEnterGameOpen(true);
  };

  // Scroll game item into view
  const scrollGameIntoView = (index: number) => {
    if (!gameListRef.current) return;
    const listElement = gameListRef.current;
    const items = listElement.querySelectorAll('li');
    if (items[index]) {
      items[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  // Navigate to previous game
  const handlePrevGame = () => {
    if (!selectedGame || games.length === 0) return;
    const currentIndex = games.findIndex((g) => g.id === selectedGame.id);
    if (currentIndex > 0) {
      const prevGame = games[currentIndex - 1];
      setSelectedGame(prevGame);
      setGameIconUrl(prevGame.iconUrl + '?t=' + Date.now());
      fetchGameHistory(prevGame.gameId);
      scrollGameIntoView(currentIndex - 1);
    }
  };

  // Navigate to next game
  const handleNextGame = () => {
    if (!selectedGame || games.length === 0) return;
    const currentIndex = games.findIndex((g) => g.id === selectedGame.id);
    if (currentIndex < games.length - 1) {
      const nextGame = games[currentIndex + 1];
      setSelectedGame(nextGame);
      setGameIconUrl(nextGame.iconUrl + '?t=' + Date.now());
      fetchGameHistory(nextGame.gameId);
      scrollGameIntoView(currentIndex + 1);
    } else if (hasNextPage && !loadingMore) {
      // At the end of current list, load more games
      loadGames(true);
    }
  };

  // Keyboard navigation with arrow keys
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't navigate if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        handlePrevGame();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        handleNextGame();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedGame, games]);

  const handleCloseEnterGame = () => {
    setIsEnterGameOpen(false);
    setEnterGameInfo(null);
    // Optionally, refresh game list or do other actions after closing
  };

  const handleEditPosition = () => {
    if (selectedGame) {
      setEditingPosition(selectedGame.id);
      setPositionInput(String(selectedGame.position));
    }
  };

  const handleCancelEditPosition = () => {
    setEditingPosition(null);
    setPositionInput(selectedGame ? String(selectedGame.position) : '');
    setPositionSuccess(false);
  };

  const handleSavePosition = async () => {
    if (!selectedGame) return;
    const newPosition = Number(positionInput);
    if (isNaN(newPosition) || newPosition < 1) {
      alert('Please enter a valid position number (minimum 1)');
      return;
    }
    if (newPosition === selectedGame.position) {
      setEditingPosition(null);
      setPositionInput(selectedGame ? String(selectedGame.position) : '');
      return;
    }
    setPositionUpdating(true);
    try {
      const updatedGame = await updateGamePosition({
        gameId: selectedGame.id,
        environmentId: selectedEnvironmentId,
        labelId: selectedLabelId,
        position: newPosition,
      });
      // Update selectedGame and games list
      setSelectedGame((prev) =>
        prev && prev.id === updatedGame.id
          ? { ...prev, position: updatedGame.position }
          : prev,
      );
      setGames((prevGames) =>
        prevGames.map((g) =>
          g.id === updatedGame.id
            ? { ...g, position: updatedGame.position }
            : g,
        ),
      );
      setPositionSuccess(true);
      setTimeout(() => {
        setEditingPosition(null);
        setPositionSuccess(false);
      }, 1500);
    } catch (error) {
      console.error('Error updating position:', error);
    } finally {
      setPositionUpdating(false);
    }
  };
  const handleChangeComment = async (comment: string, event?: React.MouseEvent) => {
    event?.stopPropagation(); // Prevent game selection when clicking toggle

    if (!selectedGame) return;
    try {
      setUpdatingGameId(selectedGame.id);
      const updatedGame = await updateGamePosition({
        gameId: selectedGame.id,
        environmentId: selectedEnvironmentId,
        labelId: selectedLabelId,
        position: selectedGame.position,
        comment: comment,
      });
      console.log({ updatedGame });

      // Update selectedGame and games list
      // setSelectedGame((prev) =>
      //   prev && prev.id === updatedGame.id
      //     ? { ...prev, comment: updatedGame.comment }
      //     : prev,
      // );
      setGames((prevGames) =>
        prevGames.map((g) =>
          g.id === updatedGame.id
            ? { ...g, comment: updatedGame.comment }
            : g,
        ),
      );
      if (selectedGame?.id === updatedGame.id) {
        setSelectedGame(updatedGame);
        setGameIconUrl(updatedGame.iconUrl + '?t=' + Date.now());
      }
      setPositionSuccess(true);
      setTimeout(() => {
        setEditingPosition(null);
        setPositionSuccess(false);
      }, 1500);
    } catch (error) {
      console.error('Error updating position:', error);
    } finally {
      setUpdatingGameId(null);
    }
  };

  const handleOrientationChange = async (orientation: string) => {
    if (!selectedGame) return;
    try {
      setUpdatingGameId(selectedGame.id);
      const updatedGame = await updateGameOrientation(selectedGame.id, orientation);
      console.log('Orientation updated:', { updatedGame });

      // Update games list with new orientation
      setGames((prevGames) =>
        prevGames.map((g) =>
          g.id === updatedGame.id
            ? { ...g, orientation: updatedGame.orientation }
            : g,
        ),
      );

      // Update selected game with new orientation
      if (selectedGame?.id === updatedGame.id) {
        setSelectedGame(updatedGame);
        setGameIconUrl(updatedGame.iconUrl + '?t=' + Date.now());
      }
    } catch (error) {
      console.error('Error updating orientation:', error);
    } finally {
      setUpdatingGameId(null);
    }
  };

  const handleCategoryTypeChange = async (categoryType: string) => {
    if (!selectedGame) return;
    try {
      setUpdatingGameId(selectedGame.id);
      const updatedGame = await updateGameCategoryType(selectedGame.id, categoryType);
      console.log('Category type updated:', { updatedGame });

      // Update games list with new category type
      setGames((prevGames) =>
        prevGames.map((g) =>
          g.id === updatedGame.id
            ? { ...g, categoryType: updatedGame.categoryType }
            : g,
        ),
      );

      // Update selected game with new category type
      if (selectedGame?.id === updatedGame.id) {
        setSelectedGame(updatedGame);
        setGameIconUrl(updatedGame.iconUrl + '?t=' + Date.now());
      }
    } catch (error) {
      console.error('Error updating category type:', error);
    } finally {
      setUpdatingGameId(null);
    }
  };

  const onUploadGameIcon = () => {
    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.click();
    }
    // const file = e.target.files[0];
    // console.log(file);
  }

  // Apply icon changes to both the games list and the selected game
  const patchGameIcon = (
    gameId: number,
    icon: Pick<Game, 'iconUrl' | 'labelIconUrl'>,
  ) => {
    setGames((prevGames) =>
      prevGames.map((g) => (g.id === gameId ? { ...g, ...icon } : g)),
    );
    setSelectedGame((prev) =>
      prev && prev.id === gameId ? { ...prev, ...icon } : prev,
    );
    setGameIconUrl(icon.iconUrl + '?t=' + Date.now());
  };

  const handleFileChange = async (e: any) => {
    const file = e.target.files[0];
    if (selectedGame) {
      let data = await fileUploadService.uploadGameAsset(file, selectedGame.providerId.toString(), selectedGame.gameId.toString());
      e.target.value = '';
      if (!data?.success || !data.iconUrl) {
        alert(data?.message || 'Failed to upload game icon');
        return;
      }
      // A label icon still overrides the new game icon for this label
      if (!selectedGame.labelIconUrl) {
        patchGameIcon(selectedGame.id, { iconUrl: data.iconUrl, labelIconUrl: null });
      }
      setDateNow(Date.now())
    }
  };

  const onUploadLabelIcon = () => {
    labelIconInputRef.current?.click();
  };

  // Upload an icon that replaces the game icon for the selected label only
  const handleLabelIconChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedGame) return;
    try {
      setLabelIconUpdating(true);
      const data = await fileUploadService.uploadGameAsset(
        file,
        selectedGame.providerId.toString(),
        selectedGame.gameId.toString(),
        undefined,
        selectedLabelId,
      );
      if (!data?.success || !data.iconUrl) {
        alert(data?.message || 'Failed to upload label icon');
        return;
      }
      patchGameIcon(selectedGame.id, { iconUrl: data.iconUrl, labelIconUrl: data.iconUrl });
    } finally {
      setLabelIconUpdating(false);
    }
  };

  // Remove the label icon so the label falls back to the game icon
  const handleResetLabelIcon = async () => {
    if (!selectedGame?.labelIconUrl) return;
    try {
      setLabelIconUpdating(true);
      const updatedGame = await updateGamePosition({
        gameId: selectedGame.id,
        environmentId: selectedEnvironmentId,
        labelId: selectedLabelId,
        position: selectedGame.position,
        // The mutation overwrites comment, so keep the current one
        comment: selectedGame.comment || undefined,
        iconUrl: '',
      });
      patchGameIcon(selectedGame.id, { iconUrl: updatedGame.iconUrl, labelIconUrl: null });
    } catch (error) {
      console.error('Error resetting label icon:', error);
      alert('Failed to reset label icon');
    } finally {
      setLabelIconUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.main}>
        <div className={styles.container}>
          <Loading
            type="spinner"
            size="large"
            text="Loading games..."
            fullScreen
          />
        </div>
      </div>
    );
  }

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        {/* Left: Provider/Game List */}
        <div className={styles.sidebar}>
          <label htmlFor="label-select" className={styles.selectLabel}>
            Label:
          </label>
          <select
            id="label-select"
            className={styles.providerSelect}
            value={selectedLabelId}
            onChange={(e) => {
              const selectedId = Number(e.target.value);
              setSelectedLabelId(selectedId);
              setSelectedLabels(
                labels.find((l) => l.id === selectedId)?.name || '',
              );
            }}
          >
            {labels.map((label) => (
              <option key={label.id} value={label.id}>
                {label.name}
              </option>
            ))}
          </select>
          {/* Provider Select */}
          <label htmlFor="provider-select" className={styles.selectLabel}>
            Provider:
          </label>
          <select
            id="provider-select"
            className={styles.providerSelect}
            value={selectedProviderId}
            onChange={(e) => {
              const selectedValue = e.target.value;
              if (selectedValue === 'ALL') {
                handleProviderSelect('ALL', 'ALL');
              } else {
                const selectedId = Number(selectedValue);
                const selectedName =
                  providers.find((p) => p.id === selectedId)?.name || '';
                handleProviderSelect(selectedId, selectedName);
              }
            }}
          >
            <option value="ALL">ALL</option>
            {providers.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </select>
          {/* Category Select */}
          <label htmlFor="category-select" className={styles.selectLabel}>
            Category:
          </label>
          <select
            id="category-select"
            className={styles.providerSelect}
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="ALL">ALL</option>
            <option value="SLOT">SLOT</option>
            <option value="FISH">FISH</option>
            <option value="TABLE_GAME">TABLE_GAME</option>
            <option value="POKER">POKER</option>
            <option value="LOTTERY">LOTTERY</option>
            <option value="LIVE">LIVE</option>
            <option value="MINI_GAME">MINI_GAME</option>
            <option value="PLINKO">PLINKO</option>
            <option value="Blackjack">BLACKJACK</option>
            <option value="Baccarat">BACCARAT</option>
            <option value="Roulette">ROULETTE</option>
            <option value="OTHER">OTHER</option>
          </select>
          {/* Brands Select */}
          <label htmlFor="brands-select" className={styles.selectLabel}>
            Brands:
          </label>
          <select
            id="brands-select"
            className={styles.providerSelect}
            value={brandname}
            onChange={(e) => setBrandname(e.target.value)}
          >
            {brands.map((brand) => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
          <div className={styles.allGamesTitle}>
            <span className={styles.allGamesTitleLabel}>
              <span role="img" aria-label="games">
                🎮
              </span>{' '}
              All Games
            </span>
            {selectedProviderId !== 'ALL' && (
              <span
                className={`${allGamesEnabled ? styles.toggleOn : styles.toggleOff} ${
                  updatingAllGames ? styles.updating : ''
                }`}
                onClick={handleToggleAllGamesStatus}
                style={{ cursor: 'pointer' }}
                title={allGamesEnabled ? 'Disable all games' : 'Enable all games'}
              />
            )}
          </div>
          <div className={styles.gameList} onScroll={handleScroll} ref={gameListRef}>
            <div style={{ position: 'relative' }}>
              <input
                placeholder='Search games...'
                className={styles.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {isSearching && (
                <span style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#313485',
                  fontSize: '0.85rem'
                }}>
                  Searching...
                </span>
              )}
            </div>
            <ol>
              {games.map((game) => (
                <li
                  key={game.id}
                  className={`${styles.gameItem} ${selectedGame?.id === game.id ? styles.selected : ''
                    }`}
                  onClick={() => handleGameSelect(game)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className={styles.gamePosition}>{game.position}</span>
                  <span className={styles.gameName}>{game.name}</span>
                  <span
                    className={`${game.isActive ? styles.toggleOn : styles.toggleOff
                      } ${updatingGameId === game.id ? styles.updating : ''}`}
                    onClick={(e) => handleToggleGameStatus(game, e)}
                    style={{ cursor: 'pointer' }}
                  ></span>
                </li>
              ))}
            </ol>
            {loadingMore && (
              <div className={styles.loadingMore}>
                <Loading
                  type="dots"
                  size="small"
                  text="Loading more..."
                  inline
                />
              </div>
            )}
          </div>
        </div>

        {/* Center: Game Details */}
        <div className={styles.detailsPanel}>
          {selectedGame ? (
            <>
              <div className={styles.gameTitle}>
                {selectedGame.name}{' '}
                <span role="img" aria-label="game">
                  🎮
                </span>
              </div>
              <div className={styles.gameImage}>
                <GameIconPreview
                  src={selectedGame.iconUrl ? gameIconUrl : null}
                  alt={selectedGame.name}
                />
              </div>
              {(user?.role?.name === 'ADMIN' ||
                authService.hasPermission('game.update')) && (
                <>
                  <div className={styles.iconRow}>
                    <label className={styles.selectLabel}>
                      Game icon (all labels)
                    </label>
                    <div className={styles.iconActions}>
                      <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.webp,.mp4,.webm,.json" style={{ display: 'none' }} onChange={handleFileChange} />
                      <button
                        className={styles.positionButton}
                        onClick={onUploadGameIcon}
                        title="Upload the game icon for all labels"
                      >
                        Upload
                      </button>
                    </div>
                  </div>
                  <div className={styles.iconRow}>
                    <label className={styles.selectLabel}>
                      {labels.find((l) => l.id === selectedLabelId)?.name || 'Label'} icon
                      {selectedGame.labelIconUrl ? ' (custom)' : ' (default)'}
                    </label>
                    <div className={styles.iconActions}>
                      <input ref={labelIconInputRef} type="file" accept=".png,.jpg,.jpeg,.webp,.mp4,.webm,.json" style={{ display: 'none' }} onChange={handleLabelIconChange} />
                      <button
                        className={styles.positionButton}
                        onClick={onUploadLabelIcon}
                        disabled={labelIconUpdating || !selectedGame.isActive}
                        title={
                          selectedGame.isActive
                            ? 'Upload an icon for this label only'
                            : 'Activate the game for this label first'
                        }
                      >
                        {labelIconUpdating ? 'Saving' : 'Upload'}
                      </button>
                      {selectedGame.labelIconUrl && (
                        <button
                          className={`${styles.positionButton} ${styles.secondary}`}
                          onClick={handleResetLabelIcon}
                          disabled={labelIconUpdating}
                          title="Use the game icon for this label"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className={styles.infoGrid}>
                <div>
                  <b>Provider</b>

                  {providers.find((p) => p.id === selectedGame.providerId)
                    ?.name || selectedGame.providerId}
                </div>
                <div>
                  <b>ID</b>
                  <span className={styles.longText} title={selectedGame.gameId}>
                    {selectedGame.gameId}
                  </span>
                  {/* {selectedGame.gameId} */}
                </div>
                <div>
                  <b>Last Use</b>

                  {gameHistory.length > 0
                    ? formatDate(gameHistory[0].createdAt)
                    : 'N/A'}
                </div>
                <div style={{ alignItems: 'center' }}>
                  <b>Position</b>
                  <div className={styles.positionContainer}>
                    {editingPosition === selectedGame.id ? (
                      <>
                        <input
                          // type="number"
                          min={1}
                          value={positionInput}
                          onChange={(e) => setPositionInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !positionUpdating) {
                              handleSavePosition();
                            } else if (e.key === 'Escape') {
                              handleCancelEditPosition();
                            }
                          }}
                          className={styles.positionInput}
                          disabled={positionUpdating}
                          placeholder="Position"
                          autoFocus
                        />
                        <button
                          onClick={handleSavePosition}
                          disabled={positionUpdating}
                          className={styles.positionButton}
                        >
                          {positionUpdating ? (
                            <span className={styles.positionLoading}>
                              Saving
                            </span>
                          ) : positionSuccess ? (
                            <span className={styles.positionSuccess}>
                              Saved!
                            </span>
                          ) : (
                            'Save'
                          )}
                        </button>
                        <button
                          onClick={handleCancelEditPosition}
                          disabled={positionUpdating}
                          className={`${styles.positionButton} secondary`}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <div
                          className={styles.positionDisplay}
                          title={`Current position: ${selectedGame.position}`}
                        >
                          <span>#{selectedGame.position}</span>
                        </div>
                        <button
                          onClick={handleEditPosition}
                          className={styles.positionButton}
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <b>Count of Playing</b>

                  {gameHistory.length}
                </div>
              </div>
              <div className={styles.statusRow}>
                <div
                  className={
                    selectedGame.isActive ? styles.active : styles.inactive
                  }
                >
                  {selectedGame.isActive ? 'Active' : 'Inactive'}
                </div>
                <div
                  className={`${selectedGame.isActive ? styles.toggleOn : styles.toggleOff
                    } ${updatingGameId === selectedGame.id ? styles.updating : ''
                    }`}
                  onClick={(e) => handleToggleGameStatus(selectedGame, e)}
                  style={{ cursor: 'pointer' }}
                ></div>
              </div>
              <div className={styles.statusRow}>
                <div
                  className={
                    selectedGame?.comment === 'HOT' ? styles.active : styles.inactive
                  }
                >
                  HOT
                </div>
                <div
                  className={`${selectedGame.comment === 'HOT' ? styles.toggleOn : styles.toggleOff
                    } ${updatingGameId === selectedGame.id ? styles.updating : ''
                    }`}
                  onClick={(e) => handleChangeComment('HOT')}
                  style={{ cursor: 'pointer' }}
                ></div>
              </div>
              <div className={styles.statusRow}>
                <div
                  className={
                    selectedGame?.comment === 'NEW' ? styles.active : styles.inactive
                  }
                >
                  NEW
                </div>
                <div
                  className={`${selectedGame.comment === 'NEW' ? styles.toggleOn : styles.toggleOff
                    } ${updatingGameId === selectedGame.id ? styles.updating : ''
                    }`}
                  onClick={(e) => handleChangeComment('NEW')}
                  style={{ cursor: 'pointer' }}
                ></div>
              </div>
              <div className={styles.statusRow}>
                <div
                  className={
                    selectedGame?.comment === 'PRO' ? styles.active : styles.inactive
                  }
                >
                  PRO
                </div>
                <div
                  className={`${selectedGame.comment === 'PRO' ? styles.toggleOn : styles.toggleOff
                    } ${updatingGameId === selectedGame.id ? styles.updating : ''
                    }`}
                  onClick={(e) => handleChangeComment('PRO')}
                  style={{ cursor: 'pointer' }}
                ></div>
              </div>
              <div className={styles.statusRow}>
                <div
                  className={
                    selectedGame?.comment === 'PRO' ? styles.active : styles.inactive
                  }
                >
                  HOT REELS
                </div>
                <div
                  className={`${selectedGame.comment === 'HOTREELS' ? styles.toggleOn : styles.toggleOff
                    } ${updatingGameId === selectedGame.id ? styles.updating : ''
                    }`}
                  onClick={(e) => handleChangeComment('HOTREELS')}
                  style={{ cursor: 'pointer' }}
                ></div>
              </div>
              <div className={styles.statusRow}>
                <label htmlFor="orientation-select" className={styles.selectLabel}>
                  GAME ORIENTATION
                </label>
                <select
                  id="orientation-select"
                  className={styles.providerSelect}
                  value={selectedGame?.orientation || 'horizontal'}
                  onChange={(e) => handleOrientationChange(e.target.value)}
                  disabled={updatingGameId === selectedGame.id}
                >
                  <option value="horizontal">Horizontal</option>
                  <option value="vertical">Vertical</option>
                </select>
              </div>
              <div className={styles.statusRow}>
                <label htmlFor="category-select" className={styles.selectLabel}>
                  Game Category
                </label>
                <select
                  id="category-select"
                  className={styles.providerSelect}
                  value={selectedGame?.categoryType || 'SLOT'}
                  onChange={(e) => handleCategoryTypeChange(e.target.value)}
                  disabled={updatingGameId === selectedGame.id}
                >
                  <option value="SLOT">SLOT</option>
                  <option value="FISH">FISH</option>
                  <option value="TABLE_GAME">TABLE_GAME</option>
                  <option value="POKER">POKER</option>
                  <option value="LOTTERY">LOTTERY</option>
                  <option value="LIVE">LIVE</option>
                  <option value="MINI_GAME">MINI_GAME</option>
                  <option value="PLINKO">PLINKO</option>
                  <option value="Blackjack">BLACKJACK</option>
                  <option value="Baccarat">BACCARAT</option>
                  <option value="Roulette">ROULETTE</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </div>
              {selectedGame && (
                <button
                  className={styles.demoButton}
                  onClick={() => handlePlayDemo(selectedGame)}
                >
                  Play Demo
                </button>
              )}
              {selectedGame.description && (
                <div className={styles.description}>
                  <b>Description:</b>
                  <br />
                  {selectedGame.description}
                </div>
              )}
            </>
          ) : (
            <div className={styles.noGameSelected}>
              <span role="img" aria-label="select">
                👆
              </span>{' '}
              Select a game to view details
            </div>
          )}
        </div>

        {/* Right: Game History */}
        <div className={styles.historyContainer}>
          <label htmlFor="environment-select" className={styles.selectLabel}>
            Environment:
          </label>
          <select
            id="environment-select"
            className={styles.labelsSelect}
            value={selectedEnvironmentId}
            onChange={(e) => {
              const selectedId = Number(e.target.value);
              setSelectedEnvironmentId(selectedId);
              if (selectedId) {
                fetchLabelsByEnvironment(selectedId).then((labelsData) => {
                  setLabels(labelsData);
                  let labelId =
                    labelsData.length > 0 ? labelsData[0].id : undefined;
                  if (
                    restoredSelections.current &&
                    restoredSelections.current.selectedLabelId !== undefined
                  ) {
                    const foundLabel = labelsData.find(
                      (l) =>
                        l.id === restoredSelections.current.selectedLabelId,
                    );
                    if (foundLabel) labelId = foundLabel.id;
                  }
                  if (labelId !== undefined) {
                    setSelectedLabelId(labelId);
                  }
                });

                // Refetch games with new environmentId
                loadGames(false); // Use loadGames for environment change
              }
            }}
          >
            <option value="">Select the Environment</option>
            {environments.map((environment) => (
              <option key={environment.id} value={environment.id}>
                {environment.name} - {environment.host}
              </option>
            ))}
          </select>
          <div className={styles.historyPanel}>
            <div className={styles.historyTitle}>
              <span role="img" aria-label="history">
                ⏳
              </span>{' '}
              Game History
            </div>
            <div className={styles.historyList}>
              {historyLoading ? (
                <div className={styles.historyItem}>
                  <Loading
                    type="dots"
                    size="small"
                    text="Loading history..."
                    inline
                  />
                </div>
              ) : gameHistory.length > 0 ? (
                gameHistory.map((item) => (
                  <div key={item.id} className={styles.historyItem}>
                    <div className={styles.historyDate}>
                      {formatDate(item.createdAt)}
                    </div>
                    <div>
                      <strong>{item.username}</strong>
                      <br />
                      Status: {item.status}
                      <br />
                      Balance: ${item.balanceStart?.toFixed(2) || 'N/A'} → $
                      {item.balanceEnd?.toFixed(2) || 'N/A'}
                      <br />
                      <span
                        style={{
                          color:
                            item.balanceEnd &&
                              item.balanceStart &&
                              item.balanceEnd >= item.balanceStart
                              ? 'green'
                              : 'red',
                          fontWeight: 'bold',
                        }}
                      >
                        {calculateProfitLoss(
                          item.balanceStart,
                          item.balanceEnd,
                        )}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.historyItem}>
                  No game history available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <EnterGameModal
        isOpen={isEnterGameOpen}
        onClose={handleCloseEnterGame}
        id={enterGameInfo?.id || 0}
        gameId={enterGameInfo?.gameId || ''}
        userId={enterGameInfo?.userId || 0}
        providerId={enterGameInfo?.providerId || 0}
        gameName={enterGameInfo?.gameName}
        position={selectedGame?.position}
        isActive={selectedGame?.isActive}
        environmentId={enterGameInfo?.environmentId || selectedEnvironmentId}
        labelId={enterGameInfo?.labelId || selectedLabelId}
      />
    </main>
  );
};

export default GameManagement;
